import { parseBlob } from "music-metadata";
const audioExtension = /\.(mp3|flac|m4a|aac|ogg|wav)$/i;
const clean = (value) =>
  String(value || "")
    .normalize("NFKC")
    .trim();
const leaf = (value) => value.split("/").pop();
const directory = (value) =>
  value.includes("/") ? value.slice(0, value.lastIndexOf("/")) : "";
const natural = (a, b) => a.localeCompare(b, "zh-CN", { numeric: true });
const numberOf = (value) =>
  Number(leaf(value).match(/^(\d{1,3})[.\s_\-]/)?.[1]) || 0;
export function filenameInfo(filename) {
  const base = leaf(filename)
    .replace(/\.[^.]+$/, "")
    .replace(/^\d{1,3}[.\s_\-]+/, "")
    .trim();
  const split = base.split(/\s+-\s+/);
  return {
    title: split.length > 1 ? split.slice(0, -1).join(" - ") : base,
    artist: split.length > 1 ? split.at(-1) : "",
    number: numberOf(filename),
  };
}
export function readSidecar(text) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim().split(/\s+-\s+/))
    .filter((p) => p.length >= 5 && Number(p[3]) > 0)
    .map((p) => ({
      credits: p[0],
      artist: p[0].split(/[、,，]/)[0].trim(),
      album: p[1],
      title: p[2],
      number: Number(p[3]),
      year: p[4],
    }));
}
export function isWordLyrics(text) {
  return text
    .split(/\r?\n/)
    .some(
      (line) =>
        /\[\d+:\d+(?:[.:]\d+)?\][^\[\]\s]+\[\d+:\d+(?:[.:]\d+)?\]/.test(line) ||
        /<\d+:\d+(?:[.:]\d+)?>/.test(line),
    );
}
async function readText(file) {
  const bytes = await file.arrayBuffer();
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder(
      new Uint8Array(bytes)[0] === 255 ? "utf-16le" : "gb18030",
    ).decode(bytes);
  }
}
function scope(path, folder) {
  return !folder || path.startsWith(folder + "/");
}
function folderAlbum(folder, rootName) {
  const name =
    leaf(folder.replace(/\/(?:CD|DISC|DISK)[ _-]*\d+$/i, "")) || rootName;
  return name.match(/[《『「](.+?)[》』」]/)?.[1] || name || "未命名专辑";
}
function albumMetadata(text) {
  const value = (key) =>
    text.match(new RegExp(`^"${key}"\\s*=\\s*"([^"]*)"`, "m"))?.[1] || "";
  const singers = text.match(/^"演唱"\s*=\s*\[([^\]]*)\]/m)?.[1]
    ?.match(/"[^"]*"/g)
    ?.map((item) => item.slice(1, -1)) || [];
  const title = [value("中文名"), value("英文名")].find(
    (item) => item && item !== "不适用",
  );
  return {
    title: clean(title),
    artist: clean(singers.join("、")),
    year: value("发行日期").match(/^\d{4}/)?.[0] || "",
  };
}
function cueMetadata(text) {
  const header = text.split(/^\s*TRACK\s+\d+/im)[0];
  return {
    title: clean(header.match(/^\s*TITLE\s+"([^"]+)"/im)?.[1]),
    artist: clean(header.match(/^\s*PERFORMER\s+"([^"]+)"/im)?.[1]),
    files: [...text.matchAll(/^\s*FILE\s+"([^"]+)"/gim)].map((m) => m[1]),
    trackCount: [...text.matchAll(/^\s*TRACK\s+\d+\s+AUDIO/gim)].length,
  };
}
function metadataFor(folder, entries, parsed) {
  const relevant = entries.filter((entry) => {
    const location = directory(entry.path);
    return location === folder || folder.startsWith(location + "/") ||
      (folder.split("/").length === 1 && location.startsWith(folder + "/"));
  });
  return relevant.map((entry) => parsed.get(entry.path)).find((info) => info?.title) || {};
}
export async function analyzeFiles(files, onProgress = () => {}) {
  const entries = Array.from(files)
    .map((file) => ({
      file,
      path: (file.webkitRelativePath || file.name).replaceAll("\\", "/"),
    }))
    .sort((a, b) => natural(a.path, b.path));
  const notices = [];
  const cueEntries = entries.filter((e) => /\.cue$/i.test(e.path));
  const tomlEntries = entries.filter((e) => /^meta\.toml$/i.test(leaf(e.path)));
  const cues = new Map();
  const tomls = new Map();
  for (const entry of [...cueEntries, ...tomlEntries]) {
    if (entry.file.size >= 2 * 1024 * 1024) continue;
    const decoded = await readText(entry.file);
    if (cueEntries.includes(entry)) cues.set(entry.path, cueMetadata(decoded));
    else tomls.set(entry.path, albumMetadata(decoded));
  }
  const allAudios = entries.filter((e) => audioExtension.test(e.path));
  const combined = new Set();
  for (const cue of cueEntries) {
    const info = cues.get(cue.path);
    if (!info || info.trackCount < 2) continue;
    const siblingTracks = allAudios.filter(
      (audio) => directory(audio.path) === directory(cue.path) &&
        numberOf(audio.path) > 0,
    );
    if (siblingTracks.length < 2) continue;
    for (const name of info.files) {
      const relative = (directory(cue.path) ? directory(cue.path) + "/" : "") + name.replaceAll("\\", "/");
      const whole = allAudios.find((audio) => audio.path.toLowerCase() === relative.toLowerCase());
      if (whole && !numberOf(whole.path)) {
        combined.add(whole.path);
        notices.push(`已跳过整轨 ${leaf(whole.path)}：CUE 列出 ${info.trackCount} 首，文件夹中已有分轨音频`);
      }
    }
  }
  const audios = allAudios.filter((e) => !combined.has(e.path));
  if (!audios.length)
    throw new Error("这个选择中没有 MP3、FLAC、M4A、AAC、OGG 或 WAV 音频");
  if (audios.length > 500) throw new Error("每批最多导入 500 首歌曲");
  const textEntries = entries.filter(
    (e) => /\.lrc$/i.test(e.path) || /^mp3tag\.txt$/i.test(leaf(e.path)),
  );
  const texts = new Map();
  for (const e of textEntries)
    if (e.file.size < 2 * 1024 * 1024)
      texts.set(e.path, await readText(e.file));
  const used = new Map();
  const tracks = [];
  const rootName = entries.some((entry) => entry.path.includes("/"))
    ? entries[0].path.split("/")[0]
    : "未命名专辑";
  const add = (entry, kind) => {
    if (entry) {
      used.set(entry.path, { ...entry, kind });
      return entry.path;
    }
    return "";
  };
  for (let i = 0; i < audios.length; i++) {
    const entry = audios[i],
      folder = directory(entry.path),
      name = filenameInfo(entry.path);
    onProgress(i + 1, audios.length, name.title);
    if (entry.file.size > 200 * 1024 * 1024)
      throw new Error("音频单文件最大 200 MiB：" + entry.file.name);
    let common = {};
    try {
      common = (
        await parseBlob(entry.file, { duration: false, skipCovers: false })
      ).common;
    } catch {
      notices.push(entry.file.name + "：标签未识别，将使用文件名");
    }
    const sidecarEntry = textEntries.find(
      (e) => /^mp3tag\.txt$/i.test(leaf(e.path)) && scope(e.path, folder),
    );
    const sidecars = sidecarEntry
      ? readSidecar(texts.get(sidecarEntry.path) || "")
      : [];
    const albumInfo = {
      ...metadataFor(folder, cueEntries, cues),
      ...Object.fromEntries(Object.entries(metadataFor(folder, tomlEntries, tomls))
        .filter(([, value]) => value)),
    };
    const number = Number(common.track?.no) || name.number || i + 1;
    const sidecar = sidecars.find((r) => r.number === number);
    const title = clean(common.title || sidecar?.title || name.title),
      artist = clean(
        common.artist || name.artist || sidecar?.artist || albumInfo.artist || "未知歌手",
      );
    const instrumental = /(?:^|\/)(?:器乐版|纯音乐版|伴奏版|instrumental)(?:\/|$)/i.test(folder);
    const releaseFolder = instrumental
      ? folder.replace(/\/(?:GIFT\/)?(?:器乐版|纯音乐版|伴奏版|instrumental)(?:\/.*)?$/i, "")
      : folder;
    let album = clean(
      common.album || sidecar?.album || albumInfo.title || folderAlbum(releaseFolder, rootName),
    );
    if (instrumental && !/(?:器乐版|instrumental)/i.test(album))
      album += "（器乐版）";
    const explicitAlbumArtist = clean(common.albumartist || albumInfo.artist);
    const albumArtist = explicitAlbumArtist || artist;
    const candidates = textEntries
      .filter((e) => /\.lrc$/i.test(e.path) && scope(e.path, folder))
      .map((e) => ({
        entry: e,
        score:
          (numberOf(e.path) === number ? 60 : 0) +
          (clean(filenameInfo(e.path).title) === title ? 100 : 0) -
          (e.path.split("/").length - entry.path.split("/").length) * 2,
        word: isWordLyrics(texts.get(e.path) || ""),
      }))
      .filter((e) => e.score > 40)
      .sort((a, b) => b.score - a.score);
    const line = candidates.find((e) => !e.word)?.entry,
      word = candidates.find((e) => e.word)?.entry;
    let cover = entries
      .filter(
        (e) => /\.(png|jpe?g|webp)$/i.test(e.path) && scope(e.path, folder),
      )
      .sort((a, b) => {
        const score = (e) =>
          (/^cover\./i.test(leaf(e.path))
            ? 100
            : /^folder\./i.test(leaf(e.path))
              ? 90
              : /^封面/i.test(leaf(e.path))
                ? 85
              : 0) -
          (e.path.split("/").length - entry.path.split("/").length) * 10;
        return score(b) - score(a);
      })[0];
    if (!cover && instrumental && releaseFolder !== folder) {
      cover = entries.find((e) =>
        /\.(png|jpe?g|webp)$/i.test(e.path) &&
        directory(e.path) === releaseFolder &&
        /^(?:cover|folder|封面)(?:[. _-]|$)/i.test(leaf(e.path))
      );
    }
    if (!cover && common.picture?.[0]) {
      const picture = common.picture[0],
        ext = picture.format.includes("png")
          ? "png"
          : picture.format.includes("webp")
            ? "webp"
            : "jpg";
      cover = {
        path:
          (folder ? folder + "/" : "") + "embedded-cover-" + number + "." + ext,
        file: new File([picture.data], "cover." + ext, {
          type: picture.format,
          lastModified: 0,
        }),
      };
    }
    if (name.title !== title)
      notices.push(`第 ${number} 首：文件名“${name.title}” → 显示名“${title}”`);
    tracks.push({
      audio: add(entry, "audio"),
      lineLyric: add(line, "lyrics"),
      wordLyric: add(word, "lyrics"),
      cover: add(cover, "cover"),
      title,
      artist,
      album,
      albumArtist,
      _explicitAlbumArtist: explicitAlbumArtist,
      year: String(common.year || sidecar?.year || albumInfo.year || ""),
      number,
      disc:
        Number(common.disk?.no) ||
        Number(folder.match(/(?:CD|DISC|DISK)[ _-]*(\d+)$/i)?.[1]) ||
        1,
      credits: sidecar?.credits || "",
      folder,
    });
  }
  // Album artist is shared by a release; song artists may differ by track.
  const releases = new Map();
  for (const track of tracks) {
    const key = `${track.folder.split("/")[0]}\0${track.album}`;
    if (!releases.has(key)) releases.set(key, []);
    releases.get(key).push(track);
  }
  for (const release of releases.values()) {
    const explicit = [...new Set(release.map((t) => t._explicitAlbumArtist).filter(Boolean))];
    const artists = [...new Set(release.map((t) => t.artist))];
    const albumArtist = explicit.length === 1 ? explicit[0] :
      artists.length === 1 ? artists[0] : "群星";
    const year = release.map((t) => t.year).find(Boolean) || "";
    for (const track of release) {
      track.albumArtist = albumArtist;
      track.year = year;
      delete track._explicitAlbumArtist;
    }
  }
  tracks.sort((a, b) =>
    natural(a.album, b.album) || a.disc - b.disc || a.number - b.number ||
    natural(a.audio, b.audio),
  );
  for (const track of tracks) {
    if (!track.lineLyric && !track.wordLyric)
      notices.push(`${track.title}：没有匹配到 LRC 歌词`);
    if (!track.cover) notices.push(`${track.title}：没有匹配到封面`);
  }
  const selected = [...used.values()].sort((a, b) =>
    a.path.localeCompare(b.path),
  );
  return {
    tracks,
    files: selected,
    notices,
    ignored:
      entries.length -
      selected.filter((e) => entries.some((x) => x.path === e.path)).length,
    totalBytes: selected.reduce((s, e) => s + e.file.size, 0),
  };
}
export async function makeManifest(preview) {
  const files = preview.files.map((e) => ({
    path: e.path,
    kind: e.kind,
    size: e.file.size,
    modified: e.file.lastModified,
  }));
  const manifest = { files, tracks: preview.tracks };
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(JSON.stringify(manifest)),
  );
  return {
    ...manifest,
    fingerprint: Array.from(new Uint8Array(digest), (v) =>
      v.toString(16).padStart(2, "0"),
    ).join(""),
  };
}
export async function libraryApi(path, body, method = body ? "POST" : "GET") {
  const response = await fetch("/music/library" + path, {
    method,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-Rhine-Request": "1" },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok)
    throw Object.assign(new Error(data.error || "音乐管理服务暂不可用"), {
      status: response.status,
    });
  return data;
}
export class ImportTransfer {
  constructor(preview, onUpdate) {
    this.preview = preview;
    this.onUpdate = onUpdate;
    this.paused = false;
    this.abort = new AbortController();
  }
  pause() {
    this.paused = true;
    this.abort.abort();
  }
  async start() {
    const previous = this.running;
    if (previous) this.pause();
    const task = (async () => {
      if (previous) await previous.catch(() => {});
      return this.upload();
    })();
    this.running = task;
    try {
      return await task;
    } finally {
      if (this.running === task) this.running = null;
    }
  }
  async upload() {
    this.paused = false;
    this.abort = new AbortController();
    const manifest = await makeManifest(this.preview);
    if (this.paused) return;
    const job = await libraryApi("/imports", manifest);
    if (this.paused) return;
    this.job = job;
    if (job.state === "published") {
      this.onUpdate({ done: true, result: job.result, already: true });
      return job.result;
    }
    const files = new Map(this.preview.files.map((e) => [e.path, e.file]));
    this.offsets = new Map(job.files.map((f) => [f.id, f.offset]));
    const progress = (name = "") =>
      this.onUpdate({
        jobId: job.id,
        total: this.preview.totalBytes,
        uploaded: [...this.offsets.values()].reduce((a, b) => a + b, 0),
        name,
        completed: job.files.filter((f) => this.offsets.get(f.id) >= f.size)
          .length,
        files: job.files.length,
      });
    const queue = job.files.filter((f) => f.state !== "complete");
    let next = 0;
    const worker = async () => {
      while (next < queue.length && !this.paused) {
        const info = queue[next++],
          file = files.get(info.path);
        if (!file) throw new Error("找不到原文件，请重新选择同一个文件夹");
        let offset = info.offset,
          failures = 0;
        while (offset < file.size && !this.paused) {
          const part = file.slice(
            offset,
            Math.min(file.size, offset + 512 * 1024),
          );
          let response;
          for (let retry = 0; retry < 3; retry++) {
            try {
              response = await fetch(
                `/music/library/imports/${job.id}/files/${info.id}`,
                {
                  method: "PATCH",
                  credentials: "same-origin",
                  headers: {
                    "Content-Type": "application/octet-stream",
                    "X-Rhine-Request": "1",
                    "X-Upload-Offset": String(offset),
                  },
                  body: part,
                  signal: this.abort.signal,
                },
              );
              if (response.ok) break;
              const data = await response.json();
              if (response.status === 409 || response.status >= 500)
                throw new Error(data.error || "上传进度暂不可用");
              throw Object.assign(new Error(data.error || "上传失败"), {
                fatal: true,
              });
            } catch (error) {
              if (this.paused || error.name === "AbortError") return;
              if (error.fatal || ++failures >= 3) throw error;
              const fresh = await libraryApi("/imports/" + job.id);
              const newOffset = fresh.files.find(
                (f) => f.id === info.id,
              ).offset;
              if (newOffset > offset) failures = 0;
              offset = newOffset;
              this.offsets.set(info.id, offset);
              if (offset >= file.size) {
                response = null;
                break;
              }
              await new Promise((resolve) =>
                setTimeout(resolve, 500 * failures),
              );
              response = null;
              break;
            }
          }
          if (response?.ok) {
            const data = await response.json();
            offset = data.offset;
            failures = 0;
            this.offsets.set(info.id, offset);
          }
          progress(info.path);
        }
      }
    };
    progress();
    try {
      const guardedWorker = () =>
        worker().catch((error) => {
          this.paused = true;
          this.abort.abort();
          throw error;
        });
      const results = await Promise.allSettled([
        guardedWorker(),
        guardedWorker(),
      ]);
      const failed = results.find((result) => result.status === "rejected");
      if (failed) throw failed.reason;
      if (this.paused) {
        this.onUpdate({ paused: true });
        return;
      }
      const result = await libraryApi("/imports/" + job.id + "/publish", {});
      this.onUpdate({ done: true, result });
      return result;
    } catch (error) {
      this.paused = true;
      this.abort.abort();
      this.onUpdate({ paused: true, error: error.message });
      throw error;
    }
  }
}
